<script>
import {
  iAlertIcon,
  iAlertMessage,
  iButton,
  iButtonStyles,
  iButtonTypes,
  iDropzone,
  iIcon,
  iSpinner,
  showToast,
} from '@inscribe/signal';
import { getFriendlyFileSize } from '@/utils/file';
import { mapGetters, mapState } from 'vuex';
import * as documentStates from '@/constants/documentStates';

export default {
  name: 'BulkUploadCard',
  components: {
    iAlertIcon,
    iAlertMessage,
    iButton,
    iDropzone,
    iIcon,
    iSpinner,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: true,
    },
    acceptedExtensions: {
      type: Array,
      required: true,
    },
    showSuccessMessage: {
      type: Boolean,
      default: false,
    },
    successMessage: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    fileIcon: {
      type: String,
      default: 'medium-file-zip',
    },
    dropzoneRef: {
      type: String,
      default: 'dropzone',
    },
    isHidden: {
      type: Boolean,
      default: false,
    },
    documentationLink: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      iButtonStyles,
      iButtonTypes,
      BULK_MAX_FILE_SIZE: 409600, // 400KB
      documentStates,
    };
  },
  computed: {
    ...mapGetters(['isUploadAccessBlocked']),
    ...mapState(['bulkUploads']),
    dropzoneSubtitle() {
      return `Only ${this.acceptedExtensions.join(', ')} files accepted - Max ${getFriendlyFileSize(
        this.BULK_MAX_FILE_SIZE,
      )}`;
    },
    bulkUploadsList() {
      return this.bulkUploads.list;
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    onFilesSelected(files) {
      this.$emit('files-selected', files);
    },
    onBadFileDetected(errorType) {
      showToast({
        title:
          errorType === 'size' ? 'File too large' : 'Unsupported file type',
        text:
          errorType === 'size'
            ? `The selected file size is too large (max allowed ${getFriendlyFileSize(
                this.BULK_MAX_FILE_SIZE,
              )})`
            : 'The selected file format is not supported',
        isError: true,
      });
    },
  },
};
</script>

<template>
  <div
    v-if="isVisible"
    class="d-flex flex-column upload-zone customers-page__bulk-upload i-mt-11"
  >
    <div class="d-flex align-items-center justify-content-between">
      <div class="d-flex flex-column">
        <h5 class="i-text-button-1 i-text-neutral-120 i-mb-0">
          {{ title }}
        </h5>
        <p class="i-text-body-2 i-text-neutral-110">
          {{ subtitle }}
          <a
            v-if="documentationLink"
            :href="documentationLink"
            target="_blank"
            rel="noopener"
            class="i-link"
            >Learn more</a
          >
        </p>
      </div>

      <i-button
        left-icon="medium-delete-1"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.GHOST"
        @button-click="onClose"
      ></i-button>
    </div>

    <i-dropzone
      :ref="dropzoneRef"
      :hidden-mode="isHidden"
      class="i-mt-8"
      title="Choose file or drag and drop"
      :sub-title="dropzoneSubtitle"
      :multi-file="false"
      :max-file-size="BULK_MAX_FILE_SIZE"
      :accepted-extensions="acceptedExtensions"
      :disabled="isUploadAccessBlocked"
      @files-selected="onFilesSelected"
      @file-size-over-limit-detected="() => onBadFileDetected('size')"
      @invalid-file-type-detected="() => onBadFileDetected('type')"
    ></i-dropzone>

    <i-alert-message
      v-if="showSuccessMessage"
      class="i-mt-9"
      type="success"
      heading="Upload Complete!"
      :message="successMessage"
    ></i-alert-message>

    <i-alert-message
      v-if="errorMessage"
      class="i-mt-9"
      type="error"
      heading="Upload Failed"
      :message="errorMessage"
    ></i-alert-message>

    <div v-if="bulkUploadsList.length > 0" class="i-mt-9 d-flex flex-column">
      <div
        v-for="upload in bulkUploadsList"
        :key="upload.uploadId"
        class="d-flex justify-content-between align-items-center i-p-9 upload"
      >
        <div class="d-flex i-gap-lg">
          <div
            class="d-flex align-items-center justify-content-center icon-wrapper"
          >
            <i-icon :symbol="fileIcon" :size="24" />
          </div>
          <div class="d-flex flex-column justify-content-center">
            <p class="i-text-button-2 i-text-neutral-120">
              {{ upload.name }}
            </p>
            <div class="i-text-body-2 i-text-neutral-100">
              <span v-if="upload.error">{{ upload.error }} </span>
              <span
                v-if="
                  (upload.uploadProgress === 0 ||
                    upload.state === documentStates.PROCESSING) &&
                  !upload.error
                "
                >{{
                  upload.success
                    ? 'Uploaded successfully. Processing...'
                    : 'Uploading and processing...'
                }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="
            (upload.uploadProgress < 100 &&
              (!upload.success || !upload.error)) ||
            (upload.uploadProgress === 100 &&
              !upload.success &&
              !upload.error) ||
            upload.state === 'PROCESSING'
          "
        >
          <i-spinner size="lg"></i-spinner>
        </div>
        <i-alert-icon
          v-if="
            upload.uploadProgress === 100 && (upload.success || upload.error)
          "
          :type="upload.success ? 'success' : 'error'"
          size="xl"
          colour="dark"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.upload {
  border: 1px solid $light-neutral-60;
  border-radius: map.get($increments, 8);

  &:not(:last-child) {
    margin-bottom: map.get($increments, 8);
  }

  .icon-wrapper {
    background: $light-primary-30;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    .i-icon {
      color: $light-primary-110 !important;
    }
  }
}
</style>
